import { Table, Input, Button, Space, Tag } from "antd";
import { SearchOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { ClubPrivacyTypeEnum, PaymentStatusEnum, BookingStatusEnum, BookingModeEnum, ViewDateFormat } from "../../../../constants/appConstant";
import Common from "../../../../helper/common";
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryParams } from "../../../../contexts/queryParamsContext";
import moment from "moment";
import LottieEmptyState from "../no-data-animation/index";

const RecentBookings = ({ bookingSummary, onRefresh }) => {
  const { params, setParams } = useQueryParams();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Parse the JSON string data
  const parsedData = useMemo(() => {
    if (!bookingSummary) return null;
    
    try {
      let result;
      // If bookingSummary is a string, parse it
      if (typeof bookingSummary === 'string') {
        result = JSON.parse(bookingSummary);
      } else {
        // If bookingSummary is already an object, return as is
        result = bookingSummary;
      }
      
      return result;
    } catch (error) {
      console.error('Error parsing usersBookingSummary data:', error);
      return null;
    }
  }, [bookingSummary]);

  // Timer-based refresh functionality
  useEffect(() => {
    const checkAndRefresh = () => {
      const now = new Date();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      
      // Check if it's xx:00 or xx:30 (with 5-second tolerance)
      if ((minutes === 0 || minutes === 30) && seconds <= 5) {
        if (onRefresh) {
          onRefresh();
          setRefreshTrigger(prev => prev + 1);
        }
      }
    };

    // Check immediately
    checkAndRefresh();

    // Set up interval to check every 30 seconds
    const interval = setInterval(checkAndRefresh, 30000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [onRefresh]);

  // Date change detection
  useEffect(() => {
    const checkDateChange = () => {
      const now = new Date();
      const currentDate = now.toDateString();
      
      // Store the date in localStorage to detect changes
      const lastDate = localStorage.getItem('userTableLastDate');
      
      if (lastDate && lastDate !== currentDate) {
        // Date has changed, refresh the table
        if (onRefresh) {
          onRefresh();
          setRefreshTrigger(prev => prev + 1);
        }
      }
      
      // Update the stored date
      localStorage.setItem('userTableLastDate', currentDate);
    };

    // Check date change every minute
    const dateInterval = setInterval(checkDateChange, 60000);
    
    // Initial check
    checkDateChange();

    return () => clearInterval(dateInterval);
  }, [onRefresh]);

  // Search functionality
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const columns = [
    { 
      title: <span className="text-xs font-medium">Court</span>, 
      dataIndex: "courtName",
      key: "courtName",
      fixed: 'left',
      width: 80,
      ...getColumnSearchProps('courtName'),
      render: (text) => (
        <span className="text-xs text-default-600">
          {text || "-"}
        </span>
      )
    },
    { 
      title: <span className="text-xs font-medium">Customer</span>, 
      dataIndex: "bookedBy",
      key: "bookedBy",
      width: 120,
      ...getColumnSearchProps('bookedBy'),
      render: (text) => (
        <span className="text-xs text-default-600">
          {text || "-"}
        </span>
      )
    },
    ...(global.myClub?.privacyType === ClubPrivacyTypeEnum.Private
      ? [{ 
          title: <span className="text-xs font-medium">Address</span>, 
          dataIndex: "playerAddress",
          key: "playerAddress",
          width: 100,
          ...getColumnSearchProps('playerAddress'),
          render: (text) => (
            <span className="text-xs text-default-600">
              {text || "-"}
            </span>
          )
        }]
      : []),
    {
      title: <span className="text-xs font-medium">Time</span>,
      dataIndex: "bookingDetail",
      key: "bookingDetail",
      width: 120,
        render: (text) => (
          <span className="text-xs text-default-600 whitespace-nowrap">
            {text || "-"}
          </span>
        )
    },
    {
      title: <span className="text-xs font-medium">Total</span>,
      dataIndex: "total",
      key: "total",
      width: 80,
      render: (text) => (
        <span className="text-xs text-default-600 font-medium">
          {text ? Common.Utility.formatNumberWithCommas(text) : "-"}
        </span>
      )
    },
    {
      title: <span className="text-xs font-medium">Remaining</span>,
      dataIndex: "remainingAmount",
      key: "remainingAmount",
      width: 90,
      render: (text) => (
        <span className="text-xs text-default-600 font-medium">
          {text ? Common.Utility.formatNumberWithCommas(text) : "-"}
        </span>
      )
    },
    {
      title: <span className="text-xs font-medium">Payment</span>,
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 80,
      render: (text, record) => {
        const paymentStatusKey = Common.Utility.getKeyByValue(PaymentStatusEnum, record.paymentStatus);
        
        switch (paymentStatusKey) {
          case "Paid":
            return (
              <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs font-medium">
                Paid
              </span>
            );
          case "Un Paid":
            return (
              <span className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-xs font-medium">
                Unpaid
              </span>
            );
          case "Partial Paid":
            return (
              <span className="bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-xs font-medium">
                Partial
              </span>
            );
          default:
            return (
              <span className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-medium">
                Unknown
              </span>
            );
        }
      },
    },
    { 
      title: <span className="text-xs font-medium">Sport</span>, 
      dataIndex: "sportName",
      key: "sportName",
      fixed: 'right',
      width: 70,
      ...getColumnSearchProps('sportName'),
      render: (text) => (
        <span className="text-xs text-default-600">
          {text || "-"}
        </span>
      )
    },
    {
      title: <span className="text-xs font-medium">Action</span>,
      key: "action",
      fixed: 'right',
      width: 50,
      align: 'right',
      render: (text, record) => (
        <div className="flex justify-end">
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
        setParams({
          ...params,
              bookingId: record.id,
        });
        navigate("/app/bookingDetails");
          }}
          size="small"
          title="View Details"
            className="text-gray-600 hover:text-blue-600"
        />
        </div>
      ),
    },
  ];

  // Process data to show all items
  //const processedData = Array.isArray(parsedData) ? parsedData : [];
  const processedData = Array.isArray(parsedData) ? parsedData.slice(0, 20) : [];

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
        <h3 className="text-lg font-semibold text-gray-900">Recent Bookings</h3>
      </div>
      <Table
        columns={columns}
        dataSource={processedData}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={processedData.length > 0 ? { x: 800, y: 318 } : { y: 280 }}
        className="ant-table-small"
        style={{height: processedData.length > 0 ? '368px' : '406px' }}
        locale={{
          emptyText: (
            <LottieEmptyState
              title="No Recent Bookings"
              subtitle="Booking data will appear here when available"
              height={260}
              width={180}
            />
          )
        }}
      />
      {processedData.length > 0 && (
        <div className="px-6 py-2 bg-gray-50 border-t border-gray-200 text-center text-sm text-gray-500">
          {/* Showing {processedData.length} bookings */}
          Showing {processedData.length} of {Array.isArray(parsedData) ? parsedData.length : 0} bookings
        </div>
      )}
    </div>
  );
};

export default RecentBookings;
